import type {
	CreatePartMutationRequest,
	UpdatePartMutationRequest,
} from "@packages/api-client/types/equipmentController";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCreatePart } from "@/api/equipment";
import { PageHeader } from "@/components/PageHeader";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { PartForm } from "./-components/PartForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/parts/new",
)({
	// Inventory management is desk work — online-only by design.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: NewPartPage,
});

function NewPartPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const createMutation = useCreatePart();

	const handleSubmit = (
		formData: CreatePartMutationRequest | UpdatePartMutationRequest,
	) =>
		createMutation
			.mutateAsync({
				organizationId: orgId,
				data: formData as CreatePartMutationRequest,
			})
			.then(() => {
				navigate({
					to: "/app/org/$orgId/equipment/parts",
					params: { orgId },
				});
			});

	return (
		<div className="flex flex-col gap-6 h-full">
			<PageHeader title={t("equipment.parts.create.title")} showBackButton />
			<PartForm
				orgId={orgId}
				onSubmit={handleSubmit}
				isSubmitting={createMutation.isPending}
				onCancel={() =>
					navigate({
						to: "/app/org/$orgId/equipment/parts",
						params: { orgId },
					})
				}
			/>
		</div>
	);
}
